import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import './MenuUrlExtractor.css';

const DEFAULT_SITE_SUPPORT = {
  supportedSites: [
    { name: 'Allrecipes', domains: ['allrecipes.com'], notes: 'Domain parser' },
    { name: 'Delish', domains: ['delish.com'], notes: 'Domain parser' },
    { name: 'TikTok', domains: ['tiktok.com'], notes: 'Domain parser' },
    { name: 'RecipeTin Eats', domains: ['recipetineats.com'], notes: 'Schema.org Recipe domain parser' },
    { name: 'BBC Good Food', domains: ['bbcgoodfood.com'], notes: 'Schema.org Recipe domain parser' },
    { name: 'EatingWell', domains: ['eatingwell.com'], notes: 'Schema.org Recipe domain parser' },
    { name: 'Food Network', domains: ['foodnetwork.com'], notes: 'Schema.org Recipe domain parser' },
    { name: 'Serious Eats', domains: ['seriouseats.com'], notes: 'Schema.org Recipe domain parser' },
    { name: 'Simply Recipes', domains: ['simplyrecipes.com'], notes: 'Schema.org Recipe domain parser' },
    { name: 'Taste of Home', domains: ['tasteofhome.com'], notes: 'Schema.org Recipe domain parser' },
    {
      name: 'Sallys Baking Addiction',
      domains: ['sallysbakingaddiction.com'],
      notes: 'Schema.org Recipe domain parser'
    },
    { name: 'The Pioneer Woman', domains: ['thepioneerwoman.com'], notes: 'Schema.org Recipe domain parser' },
    { name: 'Food & Wine', domains: ['foodandwine.com'], notes: 'Schema.org Recipe domain parser' }
  ],
  bestEffortSites: [
    {
      name: 'WordPress recipe blogs',
      domains: ['Sites using WP Recipe Maker or recipe-card style markup'],
      notes: 'Best effort structure parser'
    }
  ]
};

const getExtractorBasePath = (user) => {
  if (user?.isAdmin) {
    return '/admin';
  }

  if (user?.role === 'affiliate' || user?.isAffiliate) {
    return '/affiliate';
  }

  return '/user';
};

const normalizeExtractedMenuData = (menuData, fallbackUrl) => {
  const normalizedUrl = menuData?.url || menuData?.sourceUrl || fallbackUrl || '';
  const normalizedImage = menuData?.image || menuData?.imageUrl || menuData?.mainImageUrl || '';

  return {
    ...menuData,
    url: normalizedUrl,
    sourceUrl: menuData?.sourceUrl || normalizedUrl,
    contentUrl: menuData?.contentUrl || normalizedUrl,
    image: normalizedImage,
    imageUrl: menuData?.imageUrl || normalizedImage,
    cuisine: menuData?.cuisine || 'Other',
    ingredients: Array.isArray(menuData?.ingredients) ? menuData.ingredients : [],
    instructions: Array.isArray(menuData?.instructions) ? menuData.instructions : [],
    nutrition: menuData?.nutrition && typeof menuData.nutrition === 'object' ? menuData.nutrition : {}
  };
};

const MenuUrlExtractor = ({ onExtractSuccess }) => {
  const [url, setUrl] = useState('');
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractionStatus, setExtractionStatus] = useState('');
  const [extractionStep, setExtractionStep] = useState(0);
  const [siteSupport, setSiteSupport] = useState(DEFAULT_SITE_SUPPORT);
  const stepTimerRef = useRef(null);

  // Extraction steps for user feedback
  const extractionSteps = [
    { message: 'Sending request to website...', icon: '🌐' },
    { message: 'Analyzing page content...', icon: '🔍' },
    { message: 'Looking for recipe data...', icon: '📝' },
    { message: 'Extracting ingredients and instructions...', icon: '🥗' },
    { message: 'Processing nutrition information...', icon: '📊' },
    { message: 'Finalizing extraction...', icon: '✨' }
  ];

  // Clear timer on unmount
  useEffect(() => {
    return () => {
      if (stepTimerRef.current) {
        clearInterval(stepTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const fetchSiteSupport = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          return;
        }

        const userString = localStorage.getItem('user');
        const user = userString ? JSON.parse(userString) : null;
        const basePath = getExtractorBasePath(user);

        const response = await fetch(`${process.env.REACT_APP_API_URL}${basePath}/extract-menu-sites`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          return;
        }

        const data = await response.json();
        if (data?.status !== 'success') {
          return;
        }

        setSiteSupport({
          supportedSites: Array.isArray(data.supportedSites) && data.supportedSites.length
            ? data.supportedSites
            : DEFAULT_SITE_SUPPORT.supportedSites,
          bestEffortSites: Array.isArray(data.bestEffortSites) && data.bestEffortSites.length
            ? data.bestEffortSites
            : DEFAULT_SITE_SUPPORT.bestEffortSites
        });
      } catch (error) {
        console.error('Failed to load extractor support sites:', error);
      }
    };

    fetchSiteSupport();
  }, []);

  const startProgressSimulation = () => {
    setExtractionStep(0);
    let currentStep = 0;
    
    stepTimerRef.current = setInterval(() => {
      currentStep += 1;
      if (currentStep < extractionSteps.length) {
        setExtractionStep(currentStep);
      } else {
        // Loop back to "Analyzing..." while waiting
        setExtractionStep(1);
        currentStep = 1;
      }
    }, 2000);
  };

  const stopProgressSimulation = () => {
    if (stepTimerRef.current) {
      clearInterval(stepTimerRef.current);
      stepTimerRef.current = null;
    }
  };

  const handleExtract = async (e) => {
    e.preventDefault();
    if (!url) {
      toast.error('Please enter a URL');
      return;
    }

    setIsExtracting(true);
    setExtractionStatus('');
    startProgressSimulation();

    try {
      const token = localStorage.getItem('token');
      const userString = localStorage.getItem('user');
      const user = userString ? JSON.parse(userString) : null;
      const basePath = getExtractorBasePath(user);
      
      const endpoint = `${basePath}/extract-menu`;
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ url })
      });

      const data = await response.json();

      if (!response.ok || data.status === 'error') {
        throw new Error(data.message || 'Failed to extract recipe information');
      }
      
      // Ensure the extracted data has all required fields
      const extractedData = normalizeExtractedMenuData(data.menuData || {}, url);
      
      stopProgressSimulation();
      setExtractionStatus('Recipe extracted. Opening editor...');
      await Promise.resolve(onExtractSuccess?.(extractedData));
      setUrl('');
      setExtractionStatus('');
      toast.success('Recipe information extracted successfully. Review the form below before saving.');
    } catch (error) {
      console.error('Extraction error:', error);
      stopProgressSimulation();
      setExtractionStatus(error.message);
      toast.error(error.message);
    } finally {
      setIsExtracting(false);
    }
  };

  return (
    <div className="menu-url-extractor">
      <div className="extractor-header">
        <h3><i className="fas fa-link"></i> Extract Recipe from URL</h3>
        <p className="extractor-description">
          Paste a URL from a recipe website or cooking blog. Our system will scan the page and 
          automatically extract the recipe details including ingredients, instructions, cooking times, 
          and nutrition information when available.
        </p>
        <div className="extractor-tips">
          <span className="tip"><i className="fas fa-check-circle"></i> Works best with dedicated recipe pages</span>
          <span className="tip"><i className="fas fa-check-circle"></i> Supports most popular recipe sites</span>
          <span className="tip"><i className="fas fa-info-circle"></i> You can edit extracted data before saving</span>
        </div>
        <div className="extractor-site-support">
          <div className="extractor-site-card">
            <h4>Supported Sites</h4>
            <ul className="site-support-list">
              {siteSupport.supportedSites.map((site) => {
                const domains = Array.isArray(site.domains)
                  ? site.domains.join(', ')
                  : (site.domains || '');

                return (
                  <li key={`supported-${site.name}`}>
                    <span className="site-support-name">{site.name}</span>
                    {domains && <span className="site-support-domains">{domains}</span>}
                    {site.notes && <span className="site-support-notes">{site.notes}</span>}
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="extractor-site-card best-effort">
            <h4>Best Effort Sites</h4>
            <ul className="site-support-list">
              {siteSupport.bestEffortSites.map((site) => {
                const domains = Array.isArray(site.domains)
                  ? site.domains.join(', ')
                  : (site.domains || '');

                return (
                  <li key={`best-effort-${site.name}`}>
                    <span className="site-support-name">{site.name}</span>
                    {domains && <span className="site-support-domains">{domains}</span>}
                    {site.notes && <span className="site-support-notes">{site.notes}</span>}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
      
      <form onSubmit={handleExtract}>
        <div className="url-input-group">
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="Enter recipe URL (e.g., https://example.com/recipe/pasta)"
            className="url-input"
            disabled={isExtracting}
          />
          <button 
            type="submit" 
            className="extract-button"
            disabled={isExtracting}
          >
            {isExtracting ? (
              <>
                <span className="btn-spinner"></span>
                Extracting...
              </>
            ) : (
              <>
                <i className="fas fa-magic"></i>
                Extract Recipe
              </>
            )}
          </button>
        </div>
      </form>
      
      {isExtracting && (
        <div className="extraction-progress">
          <div className="progress-step">
            <span className="step-icon">{extractionSteps[extractionStep]?.icon}</span>
            <span className="step-message">{extractionSteps[extractionStep]?.message}</span>
          </div>
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${((extractionStep + 1) / extractionSteps.length) * 100}%` }}
            />
          </div>
        </div>
      )}
      
      {extractionStatus && !isExtracting && (
        <div className="extraction-status error">
          <i className="fas fa-exclamation-triangle"></i>
          {extractionStatus}
        </div>
      )}
      
      <div className="extractor-note">
        <i className="fas fa-lightbulb"></i>
        <span>
          <strong>Note:</strong> Not all websites can be extracted. If extraction fails, 
          try using the manual entry form or import from an image instead.
        </span>
      </div>
    </div>
  );
};

export default MenuUrlExtractor; 